/**
 * index.tsx - React Application Entry Point
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

/** Show a visible error panel instead of a blank page when React crashes in production */
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[CrisisShield] React render error:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          minHeight: '100vh', background: '#09090b', color: '#f4f4f5',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', fontFamily: 'monospace', padding: 40, gap: 16,
        }}>
          <div style={{ fontSize: 48 }}>⚠️</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f43f5e' }}>Application Error</div>
          <div style={{
            background: '#18181b', border: '1px solid #3f3f46', borderRadius: 12,
            padding: '20px 28px', maxWidth: 700, width: '100%',
          }}>
            <div style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 8 }}>
              {this.state.error.name}: {this.state.error.message}
            </div>
            <pre style={{ color: '#a1a1aa', fontSize: 11, whiteSpace: 'pre-wrap', overflowX: 'auto', margin: 0 }}>
              {this.state.error.stack?.slice(0, 1200)}
            </pre>
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 8, padding: '10px 28px', background: '#06b6d4', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}>
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Get root element
const rootElement = document.getElementById('root');

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>
  );
}
